
import { News, NewsCategory, NewsStatus } from '@/types/community/news';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  setDoc,
  QueryDocumentSnapshot
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '../types/users';
import { formatFirestoreTimestamp } from '@/utils/date';
import { removeUndefined } from '@/utils/removeUndefined';
import { loadMockData } from '@/mock';

// Constants
const NEWS_COLLECTION = 'news';

// Types
export interface NewsQueryParams {
  searchTerm?: string;
  category?: NewsCategory;
  status?: NewsStatus;
  pageSize?: number;
  page?: number;

  firestoreUser: User;
}

type NewsWithDates = Partial<News>;

// Helper functions for mock data
function filterMockData<T extends { title?: string; description?: string; category?: string; status?: string }>(
  data: T[],
  searchTerm?: string,
  category?: string,
  status?: string
): T[] {
  return data.filter(item => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (item.title && item.title.toLowerCase().includes(searchLower)) ||
        (item.description && item.description.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }
    
    if (category && item.category !== category) return false;
    if (status && item.status !== status) return false;
    
    return true;
  });
}

function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  
  return {
    data: paginatedData,
    total: data.length
  };
}

// News API
export async function getNews({
  searchTerm,
  category,
  status,
  pageSize,
  page = 1,
  firestoreUser
}: NewsQueryParams): Promise<{ news: News[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockNews = await loadMockData<News[]>('newsItems', firestoreUser.language);
    const filteredData = filterMockData(mockNews, searchTerm, category, status);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      news: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const newsRef = collection(db, NEWS_COLLECTION);
    const q = query(
      newsRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );

    const snapshot = await getDocs(q);
    const news = snapshot.docs
      .map(doc => {
        const data = doc.data() as NewsWithDates;
        const mapped: NewsWithDates = {
          ...data,
          id: doc.id,
          publishDate: data.publishDate ? formatFirestoreTimestamp(data.publishDate) ?? undefined : undefined,
          expiryDate: data.expiryDate ? formatFirestoreTimestamp(data.expiryDate) ?? undefined : undefined,
          comments: data.comments || []
        };
        // Only return if all required fields are present
        if (
          typeof mapped.title === 'string' &&
          typeof mapped.description === 'string' &&
          typeof mapped.category === 'string' &&
          typeof mapped.status === 'string' &&
          mapped.author && typeof mapped.author.id === 'string' &&
          mapped.publishDate instanceof Date &&
          mapped.workspace && typeof mapped.workspace.id === 'string'
        ) {
          return mapped as News;
        }
        return undefined;
      })
      .filter((n): n is News => !!n);

    return {
      news: news,
      total: news.length
    };
  } catch (error) {
    console.error('Error fetching news:', error);
    throw new Error('Failed to fetch news');
  }
}

export async function getNewsById(id: string, firestoreUser: User): Promise<News | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Load language-specific mock data
    const mockNews = await loadMockData<News[]>('newsItems', firestoreUser.language);
    const newsItem = mockNews.find(n => n.id === id) || null;
    return newsItem;
  }
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const newsRef = collection(db, NEWS_COLLECTION);
    const q = query(
      newsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }

    const newsData = snapshot.docs[0].data() as NewsWithDates;
    const mappedNews: NewsWithDates = {
      ...newsData,
      id: snapshot.docs[0].id,
      publishDate: newsData.publishDate ? formatFirestoreTimestamp(newsData.publishDate) ?? undefined : undefined,
      expiryDate: newsData.expiryDate ? formatFirestoreTimestamp(newsData.expiryDate) ?? undefined : undefined,
      comments: newsData.comments || []
    };
    if (
      typeof mappedNews.title === 'string' &&
      typeof mappedNews.description === 'string' &&
      typeof mappedNews.category === 'string' &&
      typeof mappedNews.status === 'string' &&
      mappedNews.author && typeof mappedNews.author.id === 'string' &&
      mappedNews.publishDate instanceof Date &&
      mappedNews.workspace && typeof mappedNews.workspace.id === 'string'
    ) {
      return mappedNews as News;
    }
    return null;
  } catch (error) {
    console.error('Error fetching news:', error);
    throw new Error('Failed to fetch news');
  }
}

export async function addNews(
  news: Omit<News, 'id'>,
  firestoreUser: User
): Promise<string> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Validate required fields
    if (!news.title?.trim()) {
      throw new Error('News title is required');
    }
    if (!news.description?.trim()) {
      throw new Error('News description is required');
    }
    if (!news.category) {
      throw new Error('News category is required');
    }
    if (!news.status) {
      throw new Error('News status is required');
    }
    if (!news.author?.id) {
      throw new Error('News author is required');
    }
    if (!news.publishDate) {
      throw new Error('News publish date is required');
    }

    const newsData = addWorkspaceAndActivity(
      {
        ...news,
        publishDate: news.publishDate,
        expiryDate: news.expiryDate,
        attachments: news.attachments || undefined,
        activityRecords: news.activityRecords || undefined
      },
      firestoreUser,
      'created'
    );

    const newsRef = collection(db, NEWS_COLLECTION);
    const newNewsRef = doc(newsRef);
    
    const finalData = removeUndefined({
      ...newsData,
      id: newNewsRef.id
    });

    await setDoc(newNewsRef, finalData);

    return newNewsRef.id;
  } catch (error: any) {
    console.error('Error adding news:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to create news');
    } else if (error.code === 'unavailable') {
      throw new Error('Service unavailable: Please try again later');
    } else if (error.code === 'invalid-argument') {
      throw new Error('Invalid data provided: Please check all required fields');
    } else if (error.message) {
      throw new Error(`Failed to add news: ${error.message}`);
    } else {
      throw new Error('Failed to add news: Unknown error occurred');
    }
  }
}

export async function updateNews(
  id: string,
  data: Partial<Omit<News, 'id'>>,
  firestoreUser: User
): Promise<void> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    if (!id) {
      throw new Error('News ID is required for update');
    }

    // Get existing news to create activity record
    const existingNews = await getNewsById(id, firestoreUser);
    if (!existingNews) {
      throw new Error('News not found');
    }

    const activityRecord = createActivityRecord('updated', data, firestoreUser);

    // Ensure we preserve existing comments if not explicitly provided
    const updateData = removeUndefined({
      ...data,
      publishDate: data.publishDate,
      expiryDate: data.expiryDate,
      attachments: data.attachments || undefined,
      comments: data.comments !== undefined ? data.comments : existingNews.comments,
      activityRecords: [...(existingNews.activityRecords || []), activityRecord]
    });

    const newsRef = collection(db, NEWS_COLLECTION);
    const q = query(
      newsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('News not found');
    }

    await updateDoc(snapshot.docs[0].ref, updateData);
  } catch (error: any) {
    console.error('Error updating news:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to update news');
    } else if (error.code === 'not-found') {
      throw new Error('News not found');
    } else if (error.message) {
      throw new Error(`Failed to update news: ${error.message}`);
    } else {
      throw new Error('Failed to update news: Unknown error occurred');
    }
  }
}

export async function deleteNews(
  id: string,
  firestoreUser: User
): Promise<void> {
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const newsRef = collection(db, NEWS_COLLECTION);
    const q = query(
      newsRef,
      where('id', '==', id),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      throw new Error('News not found');
    }

    await deleteDoc(snapshot.docs[0].ref);
  } catch (error) {
    console.error('Error deleting news:', error);
    throw new Error('Failed to delete news');
  }
}

export async function getNewsByStatus(status: NewsStatus, firestoreUser: User): Promise<News[]> {
  const { news } = await getNews({ firestoreUser });
  return news.filter(item => item.status === status);
}

export async function getNewsByCategory(category: NewsCategory, firestoreUser: User): Promise<News[]> {
  const { news } = await getNews({ firestoreUser });
  return news.filter(item => item.category === category);
}

/**
 * Likes or unlikes a news item
 * @param newsId The news ID
 * @param firestoreUser The current user
 * @returns Promise<void>
 */
export async function likeNews(newsId: string, firestoreUser: User): Promise<void> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300));
    return;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const newsRef = collection(db, NEWS_COLLECTION);
    const q = query(
      newsRef,
      where('id', '==', newsId),
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    
    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      throw new Error('News not found');
    }

    const docRef = doc(db, NEWS_COLLECTION, querySnapshot.docs[0].id);
    const newsData = querySnapshot.docs[0].data();
    
    const currentLikes = newsData.liked || { count: 0, users: [] };
    const userIndex = currentLikes.users.findIndex((user: any) => user.id === firestoreUser.id);
    
    let updatedLikes;
    if (userIndex === -1) {
      // Add like
      updatedLikes = {
        count: currentLikes.count + 1,
        users: [...currentLikes.users, {
          id: firestoreUser.id,
          name: firestoreUser.displayName || firestoreUser.email || 'Unknown'
        }]
      };
    } else {
      // Remove like
      const updatedUsers = currentLikes.users.filter((user: any) => user.id !== firestoreUser.id);
      updatedLikes = {
        count: Math.max(0, currentLikes.count - 1),
        users: updatedUsers
      };
    }

    await updateDoc(docRef, {
      liked: updatedLikes
    });
  } catch (error: any) {
    console.error('Error liking/unliking news:', error);
    throw new Error('Failed to like/unlike news');
  }
} 